<template>
  <DialogHeader>
    <template #right>
      <div
        class="font-medium text-green-900 cursor-pointer"
        @click="handleClick"
      >
        Okay
      </div>
    </template>
    Select copyright
  </DialogHeader>
  <DialogContent>
    <Breadcrumb
      :path="breadcrumbPath"
      :key="breadcrumbKey"
      :iconSize="18"
      class="text-[14px] mb-[14px]"
      @location="handleLocationUpdate"
    />

    <div class="h-[268px] overflow-y-auto">
      <div
        v-if="loading"
        class="w-full h-full flex items-center justify-center mb-5"
      >
        <Spinner :size="22" />
      </div>

      <Table.Base v-else id="SelectCopyrightTable">
        <Table.Row
          v-for="item in items"
          :key="item.id"
          :id="item.id"
          @click="handleRowClick(item)"
        >
          <Table.CheckboxCell v-slot="{ checked }">
            <Checkbox
              v-if="isFile(item)"
              :size="22"
              :model-value="checked"
              @click.stop="handleRowClick(item)"
            />
          </Table.CheckboxCell>

          <Table.Cell>
            <ThumbnailIcon
              class="mr-2.5 min-w-9 max-w-9 h-9 tablet-h:min-w-6 tablet-h:max-w-6 tablet-h:h-6"
              :icon-size="24"
              :type="getItemType(item)"
              :image-url="getItemImageUrl(item)"
            />
            <div class="w-full">
              <div v-truncate class="flex items-center">
                <div data-truncate-target>
                  {{ item.name }}
                </div>
              </div>
            </div>
          </Table.Cell>

          <Table.Cell class="min-w-[200px] hidden tablet-h:flex">
            <template v-if="isFile(item)">
              <Icon name="Dacr" :size="18" class="mr-2" />
              {{
                formatTimestamp(
                  (item as File).copyright?.registrations?.[0]?.timestamp
                ) || '- -'
              }}
            </template>
            <template v-else>- -</template>
          </Table.Cell>
        </Table.Row>
      </Table.Base>
    </div>
  </DialogContent>

  <DialogFooter>
    <UiButton
      class="w-1/2 tablet-h:w-auto mr-2.5 tablet-h:mr-2"
      @click="close()"
    >
      Cancel
    </UiButton>
    <UiButton class="w-1/2 tablet-h:w-auto" primary @click="handleClick">
      Okay
    </UiButton>
  </DialogFooter>
</template>

<script setup lang="ts">
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { graphql } from '@/shared/graphql/router'
import type { File, Item } from '@/shared/graphql/router/graphql'
import { isFile, isFolder } from '@/features/explorer/utils/itemGuards'
import { computed, ref } from 'vue'
import Breadcrumb from '@/shared/components/Breadcrumb.vue'
import { Checkbox, Icon, UiButton, ThumbnailIcon, Table } from '@dacr/ui'
import Spinner from '@/shared/components/Spinner.vue'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { formatTimestamp } from '@/shared/utils/datetime'
import { useQuery } from '@vue/apollo-composable'

const FOLDER_QUERY = graphql(`
  query SelectCopyrightFolder($path: String!) {
    folderByPath(path: $path) {
      id
      name
      path
      items(input: { first: 100 }) {
        edges {
          node {
            ... on File {
              id
              name
              type
              copyright {
                id
                registrations {
                  timestamp
                }
              }
              asset {
                previewImageUrl
              }
            }
            ... on Folder {
              id
              name
              path
              totalFiles
            }
          }
        }
      }
    }
  }
`)

const { close, triggerEvent } = useDialog()

const table = Table.useTable('SelectCopyrightTable')

const folderPath = ref('/')
const selectedItem = ref<Item>()

const variables = computed(() => ({
  path: folderPath.value,
}))

const { result, loading } = useQuery(FOLDER_QUERY, variables, {
  fetchPolicy: 'cache-and-network',
  nextFetchPolicy: 'cache-only',
  clientId: 'router',
})

const path = ref<{ name: string; path: string }[]>([
  { name: 'Dacr', path: '/' },
])
const breadcrumbPath = computed(() => path.value.map((p) => p.name))
const breadcrumbKey = computed(() => breadcrumbPath.value.join('-'))

const items = computed(
  () =>
    result.value?.folderByPath?.items?.edges?.map((e) => e.node as Item) ?? []
)

const getItemType = (item: Item) => (isFile(item) ? item.type : 'folder')

const getItemImageUrl = (item: Item) =>
  isFile(item)
    ? applyAuthorizationToken(item.asset?.previewImageUrl, [
        { key: 'width', value: 36 },
      ])
    : undefined

const handleLocationUpdate = (location: string[]) => {
  path.value = path.value.slice(0, location.length)
  folderPath.value = path.value.at(-1)!.path
}

const handleFolderClick = (item: Item) => {
  if (!isFolder(item)) return
  path.value.push({ name: item.name, path: item.path })
  folderPath.value = item.path
}

const handleRowClick = (item: Item) => {
  if (isFolder(item)) {
    return handleFolderClick(item)
  }

  table.resetSelection()
  table.toggleRow(item.id)
  selectedItem.value = item
}

const handleClick = () => {
  triggerEvent('success', selectedItem.value)
  close()
}
</script>
